import * as THREE from 'three';
import { Enemy } from './Enemy';
import { EnemyType } from './EnemyType';

/**
 * Boss enemy - powerful end-of-wave enemy
 */
export class Boss extends Enemy {
  private attackHitbox: THREE.Mesh;
  private attackAnimationProgress: number = 0;
  private attackType: string = 'melee'; // 'melee' or 'ranged'
  private weapon: THREE.Group = new THREE.Group(); // Initialize with empty group
  private crown: THREE.Group = new THREE.Group(); // Initialize with empty group
  private attackPhase: number = 0;
  
  // Special attack properties
  private specialAttackCooldown: number = 10.0;
  private lastSpecialAttackTime: number = 0;
  private isSpecialAttacking: boolean = false;
  private specialAttackDuration: number = 3.0;
  private specialAttackTimer: number = 0;
  
  /**
   * Creates a new Boss enemy
   * @param position The initial position
   * @param scene The scene to add the enemy to
   */
  constructor(position: THREE.Vector3, scene: THREE.Scene) {
    super(EnemyType.BOSS, position, scene);
    
    // Create attack hitbox (invisible)
    const hitboxGeometry = new THREE.SphereGeometry(3.0);
    const hitboxMaterial = new THREE.MeshBasicMaterial({ 
      color: 0xff0000,
      transparent: true,
      opacity: 0.0, // Invisible
      depthWrite: false
    });
    this.attackHitbox = new THREE.Mesh(hitboxGeometry, hitboxMaterial);
    this.attackHitbox.visible = false;
    this.object.add(this.attackHitbox);
    
    // Add boss-specific details to the model
    this.addModelDetails();
  }
  
  /**
   * Adds visual details to the boss model
   */
  private addModelDetails(): void {
    // Change the base model color to be more intimidating
    if (this.model.material instanceof THREE.MeshStandardMaterial) {
      this.model.material.color.set(0x660066);
      this.model.material.emissive.set(0x330033);
      this.model.material.emissiveIntensity = 0.2;
    }
    
    // Add eyes
    const eyeGeometry = new THREE.SphereGeometry(0.15, 8, 8);
    const eyeMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 }); // Glowing red eyes
    
    const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
    leftEye.position.set(0.4, 0.4, 1.3);
    this.model.add(leftEye);
    
    const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
    rightEye.position.set(-0.4, 0.4, 1.3);
    this.model.add(rightEye);
    
    // Add crown
    this.crown = this.createCrown();
    this.crown.position.set(0, 0.8, 0);
    this.model.add(this.crown);
    
    // Add weapon (staff)
    this.weapon = this.createWeapon();
    this.weapon.position.set(0.8, -0.5, 0);
    this.model.add(this.weapon);
    
    // Add armor details
    this.addArmorDetails();
    
    // Add a cape
    this.addCape();
  }
  
  /**
   * Creates the boss's crown
   * @returns The crown group
   */
  private createCrown(): THREE.Group {
    const crownGroup = new THREE.Group();
    
    // Base ring
    const ringGeometry = new THREE.TorusGeometry(0.6, 0.1, 16, 32);
    const ringMaterial = new THREE.MeshStandardMaterial({ 
      color: 0xFFD700, // Gold
      metalness: 1.0,
      roughness: 0.3
    });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.rotation.x = Math.PI / 2;
    crownGroup.add(ring);
    
    // Spikes
    const spikeGeometry = new THREE.ConeGeometry(0.1, 0.3, 4);
    const spikeMaterial = new THREE.MeshStandardMaterial({ 
      color: 0xFFD700, // Gold
      metalness: 1.0,
      roughness: 0.3
    });
    
    // Add spikes around the crown
    const spikeCount = 5;
    for (let i = 0; i < spikeCount; i++) {
      const angle = (i / spikeCount) * Math.PI * 2;
      const spike = new THREE.Mesh(spikeGeometry, spikeMaterial);
      spike.position.set(
        Math.sin(angle) * 0.6,
        0.15,
        Math.cos(angle) * 0.6
      );
      spike.rotation.x = -Math.PI / 2;
      crownGroup.add(spike);
    }
    
    // Add a gem to the front
    const gemGeometry = new THREE.OctahedronGeometry(0.15);
    const gemMaterial = new THREE.MeshStandardMaterial({ 
      color: 0xff0000,
      emissive: 0xff0000,
      emissiveIntensity: 0.5,
      metalness: 0.8,
      roughness: 0.2
    });
    const gem = new THREE.Mesh(gemGeometry, gemMaterial);
    gem.position.set(0, 0.1, 0.6);
    crownGroup.add(gem);
    
    return crownGroup;
  }
  
  /**
   * Creates the boss's weapon
   * @returns The weapon group
   */
  private createWeapon(): THREE.Group {
    const weaponGroup = new THREE.Group();
    
    // Staff shaft
    const shaftGeometry = new THREE.CylinderGeometry(0.05, 0.05, 3.0, 8);
    const shaftMaterial = new THREE.MeshStandardMaterial({ color: 0x222222 });
    const shaft = new THREE.Mesh(shaftGeometry, shaftMaterial);
    weaponGroup.add(shaft);
    
    // Staff head
    const headGeometry = new THREE.SphereGeometry(0.2, 16, 16);
    const headMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x9900ff,
      emissive: 0x9900ff,
      emissiveIntensity: 0.5,
      metalness: 0.8,
      roughness: 0.2
    });
    const head = new THREE.Mesh(headGeometry, headMaterial);
    head.position.y = 1.5;
    weaponGroup.add(head);
    
    // Energy effect around the head
    const energyGeometry = new THREE.SphereGeometry(0.3, 16, 16);
    const energyMaterial = new THREE.MeshBasicMaterial({ 
      color: 0x9900ff,
      transparent: true,
      opacity: 0.3
    });
    const energy = new THREE.Mesh(energyGeometry, energyMaterial);
    energy.position.y = 1.5;
    weaponGroup.add(energy);
    
    return weaponGroup;
  }
  
  /**
   * Adds armor details to the boss
   */
  private addArmorDetails(): void {
    // Shoulder pads
    const shoulderGeometry = new THREE.SphereGeometry(0.4, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2);
    const shoulderMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x444444,
      metalness: 0.8,
      roughness: 0.2
    });
    
    const leftShoulder = new THREE.Mesh(shoulderGeometry, shoulderMaterial);
    leftShoulder.position.set(0.8, 0.5, 0);
    leftShoulder.rotation.z = -Math.PI / 2;
    this.model.add(leftShoulder);
    
    const rightShoulder = new THREE.Mesh(shoulderGeometry, shoulderMaterial);
    rightShoulder.position.set(-0.8, 0.5, 0);
    rightShoulder.rotation.z = Math.PI / 2;
    this.model.add(rightShoulder);
    
    // Chest plate
    const chestGeometry = new THREE.BoxGeometry(1.2, 1.0, 0.3);
    const chestMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x444444,
      metalness: 0.8,
      roughness: 0.2
    });
    const chest = new THREE.Mesh(chestGeometry, chestMaterial);
    chest.position.set(0, 0, 0.9);
    this.model.add(chest);
    
    // Add spikes to shoulders
    const spikeGeometry = new THREE.ConeGeometry(0.1, 0.3, 4);
    const spikeMaterial = new THREE.MeshStandardMaterial({ color: 0x222222 });
    
    const addSpike = (parent: THREE.Object3D, x: number, y: number, z: number, rotation: THREE.Euler) => {
      const spike = new THREE.Mesh(spikeGeometry, spikeMaterial);
      spike.position.set(x, y, z);
      spike.rotation.copy(rotation);
      parent.add(spike);
    };
    
    // Add spikes to left shoulder
    addSpike(leftShoulder, 0, 0.2, 0, new THREE.Euler(0, 0, Math.PI / 2));
    addSpike(leftShoulder, 0.2, 0.1, 0, new THREE.Euler(0, 0, Math.PI / 4));
    addSpike(leftShoulder, -0.2, 0.1, 0, new THREE.Euler(0, 0, Math.PI * 3/4));
    
    // Add spikes to right shoulder
    addSpike(rightShoulder, 0, 0.2, 0, new THREE.Euler(0, 0, -Math.PI / 2));
    addSpike(rightShoulder, -0.2, 0.1, 0, new THREE.Euler(0, 0, -Math.PI / 4));
    addSpike(rightShoulder, 0.2, 0.1, 0, new THREE.Euler(0, 0, -Math.PI * 3/4));
  }
  
  /**
   * Adds a cape to the boss
   */
  private addCape(): void {
    // Create cape geometry
    const capeWidth = 1.6;
    const capeHeight = 2.0;
    const capeSegments = 8;
    
    const capeGeometry = new THREE.PlaneGeometry(capeWidth, capeHeight, 1, capeSegments);
    const capeMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x660066,
      side: THREE.DoubleSide,
      roughness: 0.8
    });
    
    const cape = new THREE.Mesh(capeGeometry, capeMaterial);
    cape.position.set(0, 0, -0.8);
    
    // Add some wave to the cape
    if (cape.geometry instanceof THREE.PlaneGeometry) {
      const positions = cape.geometry.attributes.position;
      for (let i = 0; i < positions.count; i++) {
        const y = positions.getY(i);
        const waveAmount = (capeHeight - y) / capeHeight * 0.2;
        positions.setZ(i, Math.sin(y * 5) * waveAmount);
      }
      positions.needsUpdate = true;
    }
    
    this.model.add(cape);
  }
  
  /**
   * Makes the boss attack
   */
  public attack(): void {
    if (this.isDead || !this.isActive) return;
    
    const currentTime = performance.now() / 1000;
    
    // Check if we should do a special attack
    if (currentTime - this.lastSpecialAttackTime >= this.specialAttackCooldown) {
      this.specialAttack();
      this.lastSpecialAttackTime = currentTime;
      return;
    }
    
    // Regular attack cooldown check
    if (currentTime - this.lastAttackTime < this.attackCooldown) {
      return; // Still on cooldown
    }
    
    super.attack();
    
    // Choose attack type
    this.attackType = Math.random() < 0.5 ? 'melee' : 'ranged';
    
    // Show attack hitbox for melee attacks
    if (this.attackType === 'melee') {
      this.attackHitbox.visible = true;
    }
    
    // Reset attack animation progress
    this.attackAnimationProgress = 0;
    this.attackPhase = 0;
  }
  
  /**
   * Performs a special attack
   */
  private specialAttack(): void {
    if (this.isDead || !this.isActive) return;
    
    this.isAttacking = true;
    this.isSpecialAttacking = true;
    this.specialAttackTimer = 0;
    this.animationState = 'special';
    this.animationTime = 0;
    
    console.log('Boss special attack!');
    
    // Show attack hitbox
    this.attackHitbox.visible = true;
    this.attackHitbox.scale.set(2, 2, 2); // Larger hitbox for special attack
  }
  
  /**
   * Updates the boss
   * @param deltaTime Time since last update in seconds
   * @param playerPosition The current player position
   */
  public update(deltaTime: number, playerPosition: THREE.Vector3): void {
    super.update(deltaTime, playerPosition);
    
    // Update attack animation
    if (this.isAttacking) {
      if (this.isSpecialAttacking) {
        this.updateSpecialAttackAnimation(deltaTime);
      } else {
        this.updateAttackAnimation(deltaTime);
      }
    }
    
    // Animate crown
    this.crown.rotation.y += deltaTime * 0.5;
    
    // Animate weapon energy
    if (this.weapon.children.length > 2) {
      const energy = this.weapon.children[2];
      energy.scale.x = 1 + Math.sin(performance.now() / 500) * 0.2;
      energy.scale.y = 1 + Math.sin(performance.now() / 500) * 0.2;
      energy.scale.z = 1 + Math.sin(performance.now() / 500) * 0.2;
    }
  }
  
  /**
   * Updates the attack animation
   * @param deltaTime Time since last update in seconds
   */
  private updateAttackAnimation(deltaTime: number): void {
    // Progress the attack animation
    this.attackAnimationProgress += deltaTime;
    
    if (this.attackType === 'melee') {
      this.updateMeleeAttackAnimation(deltaTime);
    } else {
      this.updateRangedAttackAnimation(deltaTime);
    }
  }
  
  /**
   * Updates the melee attack animation
   * @param deltaTime Time since last update in seconds
   */
  private updateMeleeAttackAnimation(deltaTime: number): void {
    // Normalize progress to 0-1 range
    const normalizedProgress = Math.min(this.attackAnimationProgress / 1.5, 1.0);
    
    // Animate the weapon for attack
    if (normalizedProgress < 0.3) {
      // Wind up phase - raise staff
      const windupProgress = normalizedProgress / 0.3; // 0 to 1
      this.weapon.rotation.x = -windupProgress * Math.PI / 2; // Raise up
    } else if (normalizedProgress < 0.6) {
      // Strike phase - bring staff down
      const strikeProgress = (normalizedProgress - 0.3) / 0.3; // 0 to 1
      this.weapon.rotation.x = -Math.PI / 2 + strikeProgress * Math.PI; // Swing down
      
      // Check for hit at the peak of the strike
      if (strikeProgress > 0.8 && strikeProgress < 0.9) {
        this.checkAttackHit();
      }
    } else {
      // Recovery phase - return to normal
      const recoveryProgress = (normalizedProgress - 0.6) / 0.4; // 0 to 1
      this.weapon.rotation.x = Math.PI / 2 - recoveryProgress * Math.PI / 2; // Return to normal
    }
    
    // Reset weapon rotation when attack is complete
    if (normalizedProgress >= 1.0) {
      this.weapon.rotation.x = 0;
      this.attackHitbox.visible = false;
    }
  }
  
  /**
   * Updates the ranged attack animation
   * @param deltaTime Time since last update in seconds
   */
  private updateRangedAttackAnimation(deltaTime: number): void {
    // Normalize progress to 0-1 range
    const normalizedProgress = Math.min(this.attackAnimationProgress / 1.5, 1.0);
    
    // Animate the weapon for ranged attack
    if (normalizedProgress < 0.4) {
      // Charge phase - raise staff and charge energy
      const chargeProgress = normalizedProgress / 0.4; // 0 to 1
      this.weapon.rotation.x = -chargeProgress * Math.PI / 4; // Raise slightly
      
      // Grow the energy sphere
      if (this.weapon.children.length > 2) {
        const energy = this.weapon.children[2];
        energy.scale.set(
          1 + chargeProgress * 2,
          1 + chargeProgress * 2,
          1 + chargeProgress * 2
        );
      }
    } else if (normalizedProgress < 0.6) {
      // Fire phase - thrust staff forward
      const fireProgress = (normalizedProgress - 0.4) / 0.2; // 0 to 1
      this.weapon.rotation.x = -Math.PI / 4 + fireProgress * Math.PI / 4; // Thrust forward
      
      // Fire projectile at the peak
      if (fireProgress > 0.8 && fireProgress < 0.9 && this.attackPhase === 0) {
        this.fireProjectile();
        this.attackPhase = 1; // Mark as fired
      }
      
      // Shrink the energy sphere
      if (this.weapon.children.length > 2) {
        const energy = this.weapon.children[2];
        energy.scale.set(
          3 - fireProgress * 2,
          3 - fireProgress * 2,
          3 - fireProgress * 2
        );
      }
    } else {
      // Recovery phase - return to normal
      const recoveryProgress = (normalizedProgress - 0.6) / 0.4; // 0 to 1
      this.weapon.rotation.x = recoveryProgress * -0; // Return to normal
      
      // Reset energy sphere
      if (this.weapon.children.length > 2) {
        const energy = this.weapon.children[2];
        energy.scale.set(1, 1, 1);
      }
    }
    
    // Reset weapon rotation when attack is complete
    if (normalizedProgress >= 1.0) {
      this.weapon.rotation.x = 0;
    }
  }
  
  /**
   * Updates the special attack animation
   * @param deltaTime Time since last update in seconds
   */
  private updateSpecialAttackAnimation(deltaTime: number): void {
    // Update special attack timer
    this.specialAttackTimer += deltaTime;
    
    // Normalize progress to 0-1 range
    const normalizedProgress = Math.min(this.specialAttackTimer / this.specialAttackDuration, 1.0);
    
    // Animate the special attack
    if (normalizedProgress < 0.2) {
      // Charge phase - float up and glow
      const chargeProgress = normalizedProgress / 0.2; // 0 to 1
      this.object.position.y = this.position.y + chargeProgress * 2; // Float up
      
      // Make the boss glow
      if (this.model.material instanceof THREE.MeshStandardMaterial) {
        this.model.material.emissiveIntensity = 0.2 + chargeProgress * 0.8;
      }
    } else if (normalizedProgress < 0.8) {
      // Attack phase - spin and emit energy
      const attackProgress = (normalizedProgress - 0.2) / 0.6; // 0 to 1
      
      // Spin the boss
      this.object.rotation.y += deltaTime * 10;
      
      // Check for hits periodically
      if (Math.floor(this.specialAttackTimer * 4) > this.attackPhase) {
        this.attackPhase = Math.floor(this.specialAttackTimer * 4);
        this.checkAttackHit();
      }
    } else {
      // Recovery phase - return to normal
      const recoveryProgress = (normalizedProgress - 0.8) / 0.2; // 0 to 1
      this.object.position.y = this.position.y + 2 - recoveryProgress * 2; // Float down
      
      // Reduce glow
      if (this.model.material instanceof THREE.MeshStandardMaterial) {
        this.model.material.emissiveIntensity = 1.0 - recoveryProgress * 0.8;
      }
    }
    
    // End special attack
    if (normalizedProgress >= 1.0) {
      this.isSpecialAttacking = false;
      this.isAttacking = false;
      this.attackHitbox.visible = false;
      this.attackHitbox.scale.set(1, 1, 1); // Reset hitbox size
      
      // Reset position and rotation
      this.object.position.y = this.position.y;
      
      // Reset material
      if (this.model.material instanceof THREE.MeshStandardMaterial) {
        this.model.material.emissiveIntensity = 0.2;
      }
    }
  }
  
  /**
   * Fires a projectile at the player
   */
  private fireProjectile(): void {
    console.log('Boss fired a projectile!');
    
    // In a real implementation, this would create a projectile object
    // and add it to the scene
  }
  
  /**
   * Checks if the attack hits the player
   */
  private checkAttackHit(): void {
    // This would normally use collision detection with the player
    // For now, we'll just log that an attack occurred
    console.log('Boss attack hit check!');
    
    // In a real implementation, this would check for collision with the player
    // and apply damage if a hit is detected
  }
  
  /**
   * Handles boss death
   */
  protected die(): void {
    super.die();
    
    // Play death animation - disintegrate
    this.animationState = 'death';
    this.animationTime = 0;
    
    // In a real implementation, this would play a more elaborate death animation
    // with particles and effects
    console.log('Boss defeated!');
  }
  
  /**
   * Disposes of resources
   */
  public dispose(): void {
    super.dispose();
    
    // Dispose of additional resources
    if (this.attackHitbox.geometry) {
      this.attackHitbox.geometry.dispose();
    }
    
    if (this.attackHitbox.material instanceof THREE.Material) {
      this.attackHitbox.material.dispose();
    }
    
    // Dispose of weapon and crown
    this.weapon.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (child.geometry) {
          child.geometry.dispose();
        }
        
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
    });
    
    this.crown.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (child.geometry) {
          child.geometry.dispose();
        }
        
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
    });
  }
} 